import { FunctionComponent, useEffect, useRef, useState, useLayoutEffect } from 'react'
import PublicationMenu from './PublicationMenu'
import PublicationProgressBar from './PublicationProgressBar'
import PublicationHeader from './PublicationHeader'
import PublicationArea from './PublicationArea'
import PublicationStrollToTop from './PublicationScrollToTop'
import useTagElements from './useTagElements'
import { PublicationModalData } from './useFetchPublication'

interface PublicationModalProps extends PublicationModalData {
  onCloseModal: () => void
}

const PublicationModal: FunctionComponent<PublicationModalProps> = ({ onCloseModal, ...props }) => {
  const { navContent, heading, linkToPDF, HTMLNumbers } = props
  const [showMenu, setShowMenu] = useState(false)
  const [documentHeight, setDocumentHeight] = useState(0)
  const modalRef = useRef<HTMLDivElement | null>(null)
  const htmlDocRef = useRef<HTMLDivElement | null>(null)
  const firstFocusableElementRef = useRef<HTMLElement | null>(null)
  const lastFocusableElementRef = useRef<HTMLElement | null>(null)
  const progressBarRef = useRef<HTMLDivElement | null>(null)
  const pubHeaderRef = useRef<HTMLDivElement | null>(null)
  const publicationAreaRef = useRef<HTMLDivElement>(null)
  const tocContainerRef = useRef<HTMLDivElement | null>(null)
  const printMenuRef = useRef<HTMLDivElement | null>(null)

  useTagElements(htmlDocRef.current)

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown)
    window.addEventListener('beforeprint', handleBeforePrint)
    window.addEventListener('afterprint', handleAfterPrint)
    document.body.classList.add('puboverlay')
    setFocusTrap()
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener('beforeprint', handleBeforePrint)
      window.removeEventListener('afterprint', handleAfterPrint)
      document.body.classList.remove('puboverlay')
    }
  }, [])

  useLayoutEffect(() => {
    const debounce = setTimeout(handleResize, 50)
    window.addEventListener('resize', handleResize)

    return () => {
      clearTimeout(debounce)
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  // Append the new content to #print-menu
  const handleBeforePrint = () => {
    if (printMenuRef.current && tocContainerRef.current) {
      // Clone the TOC content
      const printMenuClone = tocContainerRef.current.cloneNode(true) as HTMLElement
      // Also append directly to ensure it's in the DOM immediately
      printMenuRef.current.appendChild(printMenuClone)
    }
  }

  // empty the print-menu after printing
  const handleAfterPrint = () => {
    if (printMenuRef.current) {
      while (printMenuRef.current.firstChild) {
        printMenuRef.current.removeChild(printMenuRef.current.firstChild)
      }
    }
  }

  const handleResize = () => {
    setDocumentHeight(modalRef?.current?.offsetHeight || 0)
  }

  const setFocusTrap = () => {
    if (!modalRef.current) return
    const focusableElements = modalRef.current.querySelectorAll<HTMLElement>(
      "button, [href], input:not([disabled]), select, textarea, [tabindex]:not([tabindex='-1'])",
    )
    firstFocusableElementRef.current = focusableElements[0]
    lastFocusableElementRef.current = focusableElements[focusableElements.length - 1]
  }

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      onCloseModal()
    } else if (e.key === 'Tab') {
      if (!modalRef.current) return
      if (e.shiftKey && document.activeElement === firstFocusableElementRef.current) {
        lastFocusableElementRef.current?.focus()
        e.preventDefault()
      } else if (!e.shiftKey && document.activeElement === lastFocusableElementRef.current) {
        firstFocusableElementRef.current?.focus()
        e.preventDefault()
      }
    }
  }

  const toggleMenu = () => {
    setShowMenu(!showMenu)
  }

  return (
    <div className="modal-container article">
      <div
        id="pubModal"
        role="dialog"
        aria-label="Publikation"
        aria-modal="true"
        ref={modalRef}
        className={showMenu ? 'pub-toc-expanded' : ''}
      >
        <div className="publication-dialog">
          <div className="content">
            <PublicationMenu
              navContent={navContent}
              showMenu={showMenu}
              HTMLNumbers={HTMLNumbers}
              tocContainerRef={tocContainerRef}
            ></PublicationMenu>
            <div className="htmlpublication">
              <article>
                <PublicationHeader
                  heading={heading}
                  showMenu={showMenu}
                  linkToPDF={linkToPDF}
                  closeModal={onCloseModal}
                  toggleMenu={toggleMenu}
                  pubHeaderRef={pubHeaderRef}
                  modalRef={modalRef}
                ></PublicationHeader>
                <PublicationStrollToTop></PublicationStrollToTop>
                <PublicationArea
                  {...props}
                  htmlDocRef={htmlDocRef}
                  publicationAreaRef={publicationAreaRef}
                  printMenuRef={printMenuRef}
                ></PublicationArea>
                <PublicationProgressBar
                  tocContainerRef={tocContainerRef}
                  publicationAreaRef={publicationAreaRef}
                  documentHeight={documentHeight}
                  progressBarRef={progressBarRef}
                ></PublicationProgressBar>
              </article>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PublicationModal
